import { CustomColumn } from '@/common/utils/types';
import type { MetricSeriesThresholdConfig } from '@/common/components/organisms/metricChartList/metricChartList.types';
import { i18n } from '@/common/locale';
import {
  NodeElement,
  NamespaceElement,
  PodElement,
  ServiceElement,
  IngressElement,
  PersistentVolumeClaimResource,
  PersistentVolume,
  StorageClass,
  HpaList,
  PodElementList,
  KubernetesRouteResponse,
  Endpoint,
} from '@/api-client/kubernetes/model';
import { KUBER_DETAIL } from '@/common/define/slideDetail.define';
import { ValueOf } from '@/types/common.types';
import { TOTAL_DETAIL } from '@/common/stores/slide-detail.logic';

export const NODE_GRID_COLUMNS: CustomColumn<keyof NodeElement | 'id'>[] = [
  {
    caption: 'Node Name',
    field: 'name',
    type: 'string',
    width: 300,
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert',
    field: 'lastLevel',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'capitalize-label',
    align: 'center',
  },
  {
    caption: 'Conditions',
    field: 'conditions',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'label',
    align: 'center',
  },
  {
    caption: 'CPU Usage(%)',
    field: 'cpu',
    type: 'number',
    width: 130,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Usage(%)',
    field: 'memory',
    type: 'number',
    width: 130,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Disk Usage(%)',
    field: 'disk',
    type: 'number',
    width: 130,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'CPU Allocation(m)',
    field: 'nodeCpuAllocatable',
    type: 'number',
    width: 130,
    searchable: true,
  },
  {
    caption: 'CPU Capacity(m)',
    field: 'nodeCpuCapacity',
    type: 'number',
    width: 130,
    searchable: true,
  },
  {
    caption: 'CPU Limit(m)',
    field: 'nodeCpuLimit',
    type: 'number',
    width: 130,
    searchable: true,
  },
  {
    caption: 'CPU Request(m)',
    field: 'nodeCpuRequest',
    type: 'number',
    width: 130,
    searchable: true,
  },
  {
    caption: 'CPU Request/Alloc (%)',
    field: 'nodeCpuRequestUsagePerAlloc',
    type: 'number',
    width: 140,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Allocation',
    field: 'nodeMemoryAllocatable',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Capacity',
    field: 'nodeMemoryCapacity',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Limit',
    field: 'nodeMemoryLimit',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Request',
    field: 'nodeMemoryRequest',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Request/Alloc (%)',
    field: 'nodeMemoryRequestUsagePerAlloc',
    type: 'number',
    width: 140,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Taints',
    field: 'taints',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'Role',
    field: 'roles',
    type: 'string',
    width: 130,
    searchable: true,
  },
  {
    caption: 'Version',
    field: 'version',
    type: 'string',
    width: 90,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'business',
    field: 'services',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Cluster Name',
    field: 'clusterName',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    // NVIDIA 아이콘용 hidden 컬럼 — makeRows는 컬럼 필드만 방출하므로 컬럼 등록 없이는 슬롯에서 접근 불가.
    caption: 'GPU Monitor',
    field: 'onGpuMonitor',
    type: 'boolean',
    searchable: false,
    hide: true,
  },
];

type K8sResourceMetricField =
  | 'cpuLimit'
  | 'cpuRequest'
  | 'cpuUsed'
  | 'cpuUsagePerRequest'
  | 'cpuUsagePerLimit'
  | 'memoryLimit'
  | 'memoryRequest'
  | 'memoryUsed'
  | 'memoryUsagePerRequest'
  | 'memoryUsagePerLimit';

/** 워크로드·네임스페이스 목록 그리드가 공유하는 CPU/Memory 리소스 메트릭 컬럼 정의 */
export const K8S_RESOURCE_METRIC_COLUMNS: { [K in K8sResourceMetricField]: CustomColumn<K> } = {
  cpuLimit: {
    caption: 'CPU Limit (m)',
    field: 'cpuLimit',
    type: 'number',
    rendererType: 'digit',
    width: 130,
    searchable: true,
  },
  cpuRequest: {
    caption: 'CPU Request (m)',
    field: 'cpuRequest',
    type: 'number',
    rendererType: 'digit',
    width: 130,
    searchable: true,
  },
  cpuUsed: {
    caption: 'CPU Used (m)',
    field: 'cpuUsed',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 200,
    searchable: true,
  },
  cpuUsagePerRequest: {
    caption: 'CPU Usage/Request (%)',
    field: 'cpuUsagePerRequest',
    type: 'number',
    width: 150,
    searchable: true,
    rendererType: 'ratio',
  },
  cpuUsagePerLimit: {
    caption: 'CPU Usage/Limit (%)',
    field: 'cpuUsagePerLimit',
    type: 'number',
    width: 140,
    searchable: true,
    rendererType: 'ratio',
  },
  memoryLimit: {
    caption: 'Memory Limit',
    field: 'memoryLimit',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  memoryRequest: {
    caption: 'Memory Request',
    field: 'memoryRequest',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  memoryUsed: {
    caption: 'Memory Used',
    field: 'memoryUsed',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 200,
    searchable: true,
  },
  memoryUsagePerRequest: {
    caption: 'Memory Usage/Request (%)',
    field: 'memoryUsagePerRequest',
    type: 'number',
    width: 150,
    searchable: true,
    rendererType: 'ratio',
  },
  memoryUsagePerLimit: {
    caption: 'Memory Usage/Limit (%)',
    field: 'memoryUsagePerLimit',
    type: 'number',
    width: 140,
    searchable: true,
    rendererType: 'ratio',
  },
};

export const NAMESPACE_GRID_COLUMNS: CustomColumn<
  | keyof NamespaceElement
  | 'id'
  | 'podCount'
  | 'cpuRequest'
  | 'cpuLimit'
  | 'cpuUsed'
  | 'cpuUsagePerRequest'
  | 'memoryRequest'
  | 'memoryLimit'
  | 'memoryUsed'
  | 'memoryUsagePerRequest'
>[] = [
  {
    caption: 'Namespace Name',
    field: 'name',
    type: 'string',
    width: 200,
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Status',
    field: 'status',
    width: 100,
    rendererType: 'label',
    searchable: true,
  },
  {
    caption: 'Pods',
    field: 'podCount',
    type: 'string',
    width: 100,
    searchable: false,
    sortable: false,
  },
  {
    caption: 'Pod Count (Ready Pods)',
    field: 'readyPods',
    type: 'number',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Pod Count (Total Pods)',
    field: 'totalPods',
    type: 'number',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Labels',
    field: 'labels',
    type: 'string',
    rendererType: 'chip-cell',
    width: 150,
    searchable: true,
  },
  { ...K8S_RESOURCE_METRIC_COLUMNS.cpuRequest },
  { ...K8S_RESOURCE_METRIC_COLUMNS.cpuLimit },
  { ...K8S_RESOURCE_METRIC_COLUMNS.cpuUsed },
  { ...K8S_RESOURCE_METRIC_COLUMNS.cpuUsagePerRequest },
  { ...K8S_RESOURCE_METRIC_COLUMNS.cpuUsagePerLimit },
  { ...K8S_RESOURCE_METRIC_COLUMNS.memoryRequest },
  { ...K8S_RESOURCE_METRIC_COLUMNS.memoryLimit },
  { ...K8S_RESOURCE_METRIC_COLUMNS.memoryUsed },
  { ...K8S_RESOURCE_METRIC_COLUMNS.memoryUsagePerRequest },
  { ...K8S_RESOURCE_METRIC_COLUMNS.memoryUsagePerLimit },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    searchable: false,
    hide: true,
  },
];

export const POD_LIST_GRID_COLUMNS: CustomColumn<keyof PodElementList | 'k8sRoleType'>[] = [
  {
    caption: 'Pod Name',
    field: 'name',
    width: 350,
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert',
    field: 'lastLevel',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'capitalize-label',
    align: 'center',
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'label',
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'Containers',
    field: 'containers',
    type: 'string',
    width: 125,
    searchable: false,
  },
  {
    caption: 'Restarts Count',
    field: 'restarts',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'Controlled By',
    field: 'controlledBy',
    type: 'string',
    width: 105,
    searchable: true,
    clickable: true,
    attrs: {
      conditionalClickable: {
        use: true,
      },
    },
  },
  {
    caption: 'Node',
    field: 'node',
    type: 'string',
    width: 200,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'QoS',
    field: 'qos',
    type: 'string',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'Cpu Used',
    field: 'cpuUsed',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'Cpu Request',
    field: 'cpuRequest',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'Cpu Limit',
    field: 'cpuLimit',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'CPU Usage/Limits (%)',
    field: 'cpuPercent',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'CPU Usage/Request (%)',
    field: 'cpuUsagePerRequest',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'CPU Usage/Node Alloc (%)',
    field: 'cpuPercentPerNode',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Used',
    field: 'memoryUsed',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Request',
    field: 'memoryRequest',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Limit',
    field: 'memoryLimit',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Mem Usage/Limits (%)',
    field: 'memoryPercent',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Usage/Request (%)',
    field: 'memoryUsagePerRequest',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Mem Usage/Node Alloc (%)',
    field: 'memoryPercentPerNode',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'nodeId',
    field: 'nodeId',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'namespaceId',
    field: 'namespaceId',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'ownerUid',
    field: 'ownerUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'ownerName',
    field: 'ownerName',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Application Type',
    field: 'applicationType',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'k8s Role Type',
    field: 'k8sRoleType',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Business',
    field: 'services',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Deployment',
    field: 'deployment',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'ReplicaSet',
    field: 'replicaSet',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'DaemonSet',
    field: 'daemonSet',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'StatefulSet',
    field: 'statefulSet',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    // NVIDIA 아이콘용 hidden 컬럼 — makeRows는 컬럼 필드만 방출하므로 컬럼 등록 없이는 슬롯에서 접근 불가.
    caption: 'GPU Monitor',
    field: 'onGpuMonitor',
    type: 'boolean',
    searchable: false,
    hide: true,
  },
];

export const POD_DETAIL_GRID_COLUMNS: CustomColumn<keyof PodElement>[] = [
  {
    caption: 'Pod Name',
    field: 'name',
    width: 350,
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    searchable: true,
  },
  {
    caption: 'Containers',
    field: 'containers',
    type: 'string',
    width: 120,
    searchable: false,
  },
  {
    caption: 'Alert',
    field: 'lastLevel',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'capitalize-label',
    align: 'center',
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'label',
  },
  {
    caption: 'Controlled By',
    field: 'controlledBy',
    type: 'string',
    width: 105,
    searchable: true,
  },
  {
    caption: 'QoS',
    field: 'qos',
    type: 'string',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Cpu Used',
    field: 'cpuUsed',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'Cpu Request',
    field: 'cpuRequest',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'Cpu Limit',
    field: 'cpuLimit',
    type: 'number',
    width: 80,
    searchable: true,
  },
  {
    caption: 'CPU Usage/Limits (%)',
    field: 'cpuPercent',
    type: 'number',
    width: 145,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'CPU Usage/Request (%)',
    field: 'cpuUsagePerRequest',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'CPU Usage/Node Alloc (%)',
    field: 'cpuPercentPerNode',
    type: 'number',
    width: 145,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Used',
    field: 'memoryUsed',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Request',
    field: 'memoryRequest',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Memory Limit',
    field: 'memoryLimit',
    type: 'number',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Mem Usage/Limits (%)',
    field: 'memoryPercent',
    type: 'number',
    width: 145,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Memory Usage/Request (%)',
    field: 'memoryUsagePerRequest',
    type: 'number',
    width: 145,
    decimal: 3,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Mem Usage/Node Alloc (%)',
    field: 'memoryPercentPerNode',
    type: 'number',
    width: 145,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Restarts Count',
    field: 'restarts',
    type: 'number',
    width: 90,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    hide: true,
  },
  {
    // NVIDIA 아이콘용 hidden 컬럼 — makeRows는 컬럼 필드만 방출하므로 컬럼 등록 없이는 슬롯에서 접근 불가.
    caption: 'GPU Monitor',
    field: 'onGpuMonitor',
    type: 'boolean',
    searchable: false,
    hide: true,
  },
];

export const HPA_GRID_COLUMNS: CustomColumn<
  keyof HpaList | 'referenceUid' | 'referenceKind' | 'metricCurrent' | 'metricTarget'
>[] = [
  {
    caption: 'HPA Name',
    field: 'name',
    width: 250,
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 250,
    clickable: true,
    searchable: true,
  },
  {
    caption: 'Label',
    field: 'labels',
    type: 'string',
    rendererType: 'chip-cell',
    searchable: true,
  },
  {
    caption: 'Current/Target',
    field: 'metric',
    type: 'string',
    width: 130,
    searchable: false,
    align: 'right',
  },
  {
    caption: 'metric (current)',
    field: 'metricCurrent',
    type: 'number',
    searchable: true,
    hide: true,
  },
  {
    caption: 'metric (target)',
    field: 'metricTarget',
    type: 'number',
    searchable: true,
    hide: true,
  },
  {
    caption: 'Reference',
    field: 'reference',
    type: 'string',
    width: 250,
    searchable: true,
  },
  {
    caption: 'Min Replicas',
    field: 'minReplicas',
    type: 'number',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Max Replicas',
    field: 'maxReplicas',
    type: 'number',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Replicas',
    field: 'replicas',
    type: 'number',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Namespace UID',
    field: 'namespaceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Reference UID',
    field: 'referenceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Reference Kind',
    field: 'referenceKind',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const SERVICE_GRID_COLUMNS: CustomColumn<keyof ServiceElement>[] = [
  {
    caption: 'Service Name',
    field: 'name',
    width: 250,
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    clickable: true,
    searchable: true,
  },
  {
    caption: 'Type',
    field: 'type',
    type: 'string',
    width: 130,
    searchable: true,
  },
  {
    caption: 'Cluster IP',
    field: 'clusterIp',
    type: 'string',
    width: 130,
    searchable: true,
  },
  {
    caption: 'Ports',
    field: 'ports',
    type: 'string',
    width: 350,
    searchable: true,
  },
  {
    caption: 'External IP',
    field: 'externalIp',
    type: 'string',
    width: 130,
    searchable: true,
  },
  {
    caption: 'Selector',
    field: 'selector',
    type: 'string',
    width: 450,
    searchable: true,
  },
  {
    caption: 'Error Rate (%)',
    field: 'errorRate',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
    unit: {
      dataType: 'string',
      value: '%',
    },
  },
  {
    caption: 'Avg Response Latency (ms)',
    field: 'avgResponseLatencyTime',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    // Envoy 전용 P95 응답시간 지표 (HAProxy 서비스는 빈 배열)
    caption: 'P95 Response Time (ms)',
    field: 'p95ResponseTime',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    caption: 'HTTP Req/s',
    field: 'httpRequestsPerSec',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    caption: 'Active Connections',
    field: 'activeConnections',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'label',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Namespace UID',
    field: 'namespaceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const INGRESS_GRID_NAME_COLUMN: CustomColumn<keyof IngressElement> = {
  caption: 'Ingress Name',
  field: 'name',
  type: 'string',
  width: 250,
  fixed: true,
  clickable: true,
  searchable: true,
  defaultSearchField: true,
};

export const INGRESS_GRID_KIND_COLUMNS: CustomColumn<keyof Endpoint> = {
  caption: 'Kind',
  field: 'kind',
  type: 'string',
  rendererType: 'chip-cell',
  align: 'center',
  width: 80,
  searchable: true,
};

export const INGRESS_GRID_COLUMNS: CustomColumn<
  keyof IngressElement | 'location' | 'service' | 'serviceUid'
>[] = [
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Location',
    field: 'location',
    type: 'string',
    width: 450,
    searchable: true,
  },
  {
    caption: 'Service',
    field: 'service',
    type: 'string',
    width: 350,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Namespace UID',
    field: 'namespaceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Service UID',
    field: 'serviceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
];
export const DETAIL_INGRESS_GRID_ADDITIONAL_COLUMNS: CustomColumn<keyof Endpoint>[] = [
  {
    caption: 'Error Rate (%)',
    field: 'errorRate',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
    unit: {
      dataType: 'string',
      value: '%',
    },
  },
  {
    caption: 'Avg Response Latency (ms)',
    field: 'avgResponseLatencyTime',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    caption: 'HTTP Req/s',
    field: 'httpRequestsPerSec',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
  {
    caption: 'Active Connections',
    field: 'activeConnections',
    type: 'object',
    rendererType: 'trend-chart-cell',
    width: 120,
    searchable: true,
  },
];

export const OPENSHIFT_ROUTE_GRID_COLUMNS: CustomColumn<
  keyof KubernetesRouteResponse | 'location' | 'service' | 'serviceUid'
>[] = [
  {
    caption: 'Route Name',
    field: 'name',
    type: 'string',
    width: 200,
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    clickable: true,
    searchable: true,
  },
  {
    caption: 'Location',
    field: 'location',
    type: 'string',
    width: 500,
    searchable: true,
  },
  {
    caption: 'Service',
    field: 'service',
    type: 'string',
    width: 250,
    searchable: true,
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 90,
    align: 'center',
    searchable: true,
  },
  {
    caption: 'Certificate expiration',
    field: 'certificateExpiration',
    type: 'string',
    width: 140,
    searchable: true,
  },
  // {
  //   caption: 'HTTP Status',
  //   field: 'httpStatus',
  //   type: 'stringNumber',
  //   width: 80,
  //   align: 'center',
  //   searchable: true,
  // },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Namespace UID',
    field: 'namespaceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Service UID',
    field: 'serviceUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const PVC_GRID_COLUMNS: CustomColumn<keyof PersistentVolumeClaimResource>[] = [
  {
    caption: 'PVC Name',
    field: 'name',
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
    ignoreNameSize: true,
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    rendererType: 'label',
    searchable: true,
  },
  {
    caption: 'Namespace',
    field: 'namespace',
    type: 'string',
    width: 200,
    searchable: true,
  },
  {
    caption: 'PV Name',
    field: 'pvName',
    type: 'string',
    width: 200,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'PV ID',
    field: 'pvId',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Storage Classes',
    field: 'storageClasses',
    type: 'string',
    width: 200,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'Storage Used',
    field: 'storageUsed',
    type: 'number',
    width: 120,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Storage Available',
    field: 'storageAvailable',
    type: 'number',
    width: 120,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Storage Total',
    field: 'storageTotal',
    type: 'number',
    width: 120,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Storage Usage (%)',
    field: 'storageUsage',
    type: 'number',
    width: 130,
    searchable: true,
    rendererType: 'ratio',
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'Alert',
    field: 'lastLevel',
    type: 'string',
    width: 100,
    searchable: true,
    rendererType: 'capitalize-label',
    align: 'center',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Storage Class Id',
    field: 'storageClassUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const PV_GRID_COLUMNS: CustomColumn<keyof PersistentVolume>[] = [
  {
    caption: 'PV Name',
    field: 'name',
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
    ignoreNameSize: true,
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    rendererType: 'label',
    searchable: true,
  },
  {
    caption: 'Storage Classes',
    field: 'storageClasses',
    type: 'string',
    width: 200,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'PVC Name',
    field: 'pvc',
    type: 'string',
    width: 300,
    searchable: true,
    clickable: true,
  },
  {
    caption: 'Capacity',
    field: 'capacity',
    type: 'number',
    width: 130,
    searchable: true,
    specificFilterFnKey: 'readableBytes',
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Storage Class Id',
    field: 'storageClassUid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'PVC ID',
    field: 'pvcId',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const SC_GRID_COLUMNS: CustomColumn<keyof StorageClass>[] = [
  {
    caption: 'SC Name',
    field: 'name',
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
    ignoreNameSize: true,
  },
  {
    caption: 'Provisioner',
    field: 'provisioner',
    searchable: true,
    align: 'left',
  },
  {
    caption: 'Reclaim Policy',
    field: 'reclaimPolicy',
    width: 100,
    searchable: true,
    align: 'left',
  },
  {
    caption: 'Default',
    field: 'storageClassDefault',
    width: 100,
    searchable: true,
    type: 'boolean',
    align: 'left',
  },
  {
    caption: 'Age',
    field: 'age',
    type: 'age',
    width: 80,
    searchable: true,
    specificFilterFnKey: 'age',
  },
  {
    caption: 'UID',
    field: 'uid',
    searchable: false,
    hide: true,
  },
];

export const REFERENCE_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'Kind',
    field: 'roleReferenceKind',
    type: 'string',
    width: 150,
    searchable: true,
  },
  {
    caption: 'Name',
    field: 'roleReferenceName',
    type: 'string',
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'API Group',
    field: 'roleReferenceApiGroup',
    type: 'string',
    searchable: true,
  },
];

export const BINDINGS_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'Type',
    field: 'subjectKind',
    type: 'string',
    width: 200,
    searchable: true,
  },
  {
    caption: 'Name',
    field: 'subjectName',
    type: 'string',
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Namespace',
    field: 'subjectNamespace',
    type: 'string',
    searchable: true,
  },
];

export const PVS_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'PV Name',
    field: 'name',
    type: 'string',
    searchable: true,
    defaultSearchField: true,
    ignoreNameSize: true,
    clickable: true,
  },
  {
    caption: 'PV UID',
    field: 'uid',
    type: 'string',
    searchable: false,
    hide: true,
  },
  {
    caption: 'Capacity',
    field: 'capacity',
    type: 'string',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Status',
    field: 'status',
    type: 'string',
    width: 100,
    rendererType: 'label',
    searchable: true,
  },
  {
    caption: 'PVC Name',
    field: 'pvc',
    type: 'string',
    searchable: true,
    clickable: true,
  },
  {
    caption: 'PVC UID',
    field: 'pvcId',
    type: 'string',
    searchable: false,
    hide: true,
  },
];

export const DEFAULT_PAGE_INFO = {
  pageNum: 1,
  pageSize: 20,
  pages: 1,
  totalRow: 0,
} as const;

export const TRANSLATED_TEXTS = {
  // ETC
  get VIEW_DETAIL() {
    return i18n.global.t('WORD.VIEW_DETAIL');
  },
  get NO_DATA() {
    return i18n.global.t('MESSAGE.NO_DATA');
  },
  get STAT() {
    return i18n.global.t('WORD.STAT');
  },
  get NODE_STAT() {
    return i18n.global.t('WORD.NODE_STAT');
  },
  get CLUSTER() {
    return i18n.global.t('WORD.CLUSTER');
  },
  get DISABLED() {
    return i18n.global.t('WORD.DISABLED');
  },
  // TAB
  get INFORMATION_TAB_TITLE() {
    return i18n.global.t('WORD.INFORMATION');
  },
  get EVENT_TAB_TITLE() {
    return i18n.global.t('WORD.EVENT');
  },
  get POD_TAB_TITLE() {
    return i18n.global.t('WORD.POD');
  },
  get PROCESS_TAB_TITLE() {
    return i18n.global.t('WORD.PROCESS');
  },
  get METRIC_TAB_TITLE() {
    return i18n.global.t('WORD.METRIC');
  },
  get DISK_TAB_TITLE() {
    return i18n.global.t('WORD.DISK');
  },
  get ACTIVE_TAB_TITLE() {
    return i18n.global.t('WORD.ACTIVES');
  },
  get TRANSACTION_TAB_TITLE() {
    return i18n.global.t('WORD.TRANSACTION');
  },
  get DATA_SOURCE_TAB_TITLE() {
    return i18n.global.t('WORD.DATA_SOURCE');
  },
  get LOG_TAB_TITLE() {
    return i18n.global.t('WORD.LOG');
  },
  get CONTAINER_TAB_TITLE() {
    return i18n.global.t('WORD.CONTAINER');
  },
  get IMAGE_TAB_TITLE() {
    return i18n.global.t('WORD.IMAGE');
  },
  PVC_TAB_TITLE: 'PVC',
  ENDPOINTS_TAB_TITLE: 'EndPoints',
  get ALERT_TAB_TITLE() {
    return i18n.global.t('WORD.ALERT');
  },
  get HPA_TAB_TITLE() {
    return i18n.global.t('WORD.HPA_FULL_TEXT');
  },
  get EVENT_HISTORY_TAB_TITLE() {
    return i18n.global.t('WORD.EVENT_HISTORY');
  },
  get WORKLOAD_HISTORY_TAB_TITLE() {
    return i18n.global.t('WORD.WORKLOAD_HISTORY');
  },
  get SUMMARY_TAB_TITLE() {
    return i18n.global.t('WORD.SUMMARY2');
  },
  // GRID
  get EVENT_GRID_TITLE() {
    return i18n.global.t('WORD.EVENT_LIST');
  },
  get CONTAINER_GRID_TITLE() {
    return `${i18n.global.t('WORD.CONTAINER')} ${i18n.global.t('WORD.LIST')}`;
  },
  get POD_GRID_TITLE() {
    return i18n.global.t('WORD.POD_LIST');
  },
  get IMAGE_GRID_TITLE() {
    return `${i18n.global.t('WORD.IMAGE')} ${i18n.global.t('WORD.LIST')}`;
  },
  get NODE_DISK_USAGE_TITLE() {
    return `${i18n.global.t('WORD.NODE')} ${i18n.global.t('WORD.DISK')} ${i18n.global.t('WORD.USAGE')}`;
  },
  get SERVICE_ENDPOINT_GRID_TITLE() {
    return i18n.global.t('WORD.SERVICE_CONNECTED_POD_STATUS');
  },
  get NOT_READY_ENDPOINT_TREND() {
    return i18n.global.t('WORD.NOT_READY_ENDPOINT_TREND');
  },
  get NOT_READY_ENDPOINT_TREND_DESC() {
    return i18n.global.t('WORD.NOT_READY_ENDPOINT_TREND_DESC');
  },
  get JOB_GRID_TITLE() {
    return i18n.global.t('WORD.JOB_LIST');
  },
  get LOADBALANCER_GRID_TITLE() {
    return i18n.global.t('WORD.LOADBALANCER_LIST');
  },
  get PV_GRID_TITLE() {
    return i18n.global.t('WORD.PERSISTENT_VOLUME_LIST');
  },
  get BINDING_GRID_TITLE() {
    return i18n.global.t('WORD.BINDING_LIST');
  },
  get REFERENCE_GRID_TITLE() {
    return i18n.global.t('WORD.REFERENCE_LIST');
  },
  get NODE_GRID_TITLE() {
    return i18n.global.t('WORD.NODE_LIST');
  },
  get NAMESPACE_GRID_TITLE() {
    return i18n.global.t('WORD.NAMESPACE_LIST');
  },
  get SERVICE_GRID_TITLE() {
    return i18n.global.t('WORD.SERVICE_LIST');
  },
  get INGRESS_GRID_TITLE() {
    return i18n.global.t('WORD.INGRESS_LIST');
  },
  get PVC_GRID_TITLE() {
    return i18n.global.t('WORD.PERSISTENT_VOLUME_CLAIM_LIST');
  },
  get SC_GRID_TITLE() {
    return i18n.global.t('WORD.STORAGE_CLASS_LIST');
  },
  get PVC_DETAIL_GRID_TITLE() {
    return i18n.global.t('WORD.LIST_VALUE', { value: 'PVC' });
  },
  // DETAIL INFORMATION
  get SERVICE_DETAIL_INFO_TITLE() {
    return i18n.global.t('WORD.SERVICE');
  },
  get PARAMETER_INFO_TITLE() {
    return `Parameter ${i18n.global.t('WORD.INFORMATION')}`;
  },
  get POD_RESOURCE_STAT_TITLE() {
    return i18n.global.t('WORD.RELATED_RESOURCE');
  },
  get POD_INFO_TITLE() {
    return i18n.global.t('WORD.POD');
  },
  get NODE_INFO_TITLE() {
    return i18n.global.t('WORD.NODE');
  },
  get SELECTOR_INFO_TITLE() {
    return `Selector ${i18n.global.t('WORD.INFORMATION')}`;
  },
  get CLAIM_INFO_TITLE() {
    return `Claim ${i18n.global.t('WORD.INFORMATION')}`;
  },
  get EVENT_INFORMATION_TITLE() {
    return i18n.global.t('WORD.EVENT_INFORMATION');
  },
};

export const ownerReferenceToDetailTypeMap: Record<string, ValueOf<typeof KUBER_DETAIL>> = {
  // NOTE: be에서 단복수 혼용 사용 정리좀 하고 나면 복수형 제거 필요
  deployment: KUBER_DETAIL.DEPLOYMENT,
  deployments: KUBER_DETAIL.DEPLOYMENT,
  statefulset: KUBER_DETAIL.STATEFUL_SET,
  statefulsets: KUBER_DETAIL.STATEFUL_SET,
  daemonset: KUBER_DETAIL.DAEMON_SET,
  daemonsets: KUBER_DETAIL.DAEMON_SET,
  replicaset: KUBER_DETAIL.REPLICA_SET,
  replicasets: KUBER_DETAIL.REPLICA_SET,
  job: KUBER_DETAIL.JOB,
  jobs: KUBER_DETAIL.JOB,
  cronjob: KUBER_DETAIL.CRON_JOB,
  cronjobs: KUBER_DETAIL.CRON_JOB,

  // 제품에서 owner로 처리하고 상세 연계 가능
  node: KUBER_DETAIL.NODE,
};

// 성능분석쪽 테이블 API list에서 주는 kind랑 타겟 검증
export const OBJECT_KIND_TO_KUBERNETES_DETAIL = {
  pod: TOTAL_DETAIL.POD,
  node: TOTAL_DETAIL.NODE,
  deployment: TOTAL_DETAIL.DEPLOYMENT,
  replicaset: TOTAL_DETAIL.REPLICA_SET,
  statefulset: TOTAL_DETAIL.STATEFUL_SET,
  daemonset: TOTAL_DETAIL.DAEMON_SET,
  horizontalpodautoscaler: TOTAL_DETAIL.HPA,
  job: TOTAL_DETAIL.JOB,
  service: TOTAL_DETAIL.SERVICE,
  configmap: TOTAL_DETAIL.CONFIG_MAPS,
  persistentvolume: TOTAL_DETAIL.PERSISTENT_VOLUMES,
  persistentvolumeclaim: TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS,
  storageclass: TOTAL_DETAIL.STORAGE_CLASSES,
  ingress: TOTAL_DETAIL.INGRESS,
  cronjob: TOTAL_DETAIL.CRON_JOB,
  role: TOTAL_DETAIL.ROLES,
  rolebinding: TOTAL_DETAIL.ROLES_BINDINGS,
  clusterrole: TOTAL_DETAIL.CLUSTER_ROLES,
  clusterrolebinding: TOTAL_DETAIL.CLUSTER_ROLES_BINDINGS,
  serviceaccount: TOTAL_DETAIL.SERVICE_ACCOUNTS,
  container: TOTAL_DETAIL.CONTAINER_HOST,
};

export const METRIC_THRESHOLD_LINES: Record<'request' | 'limit', MetricSeriesThresholdConfig> = {
  request: { color: '#1a5ee6', labelKey: 'WORD.REQUEST' },
  limit: { color: '#e51919', labelKey: 'WORD.LIMIT' },
};
